import { Box, BoxProps } from '@chakra-ui/react'
import NewChevronDown from 'components/svg/NewChevronDown'
import { Body1, H2, H3 } from 'components/typography'
import { FC, ReactNode } from 'react'

import { FaqItem } from '../types'

export interface FaqAccordionProps extends Omit<BoxProps, 'title'> {
  /** The same array must be passed to `buildFaqPage` / `buildPageGraph({ faq })` (PRD §54). */
  items: FaqItem[]
  /** Optional section heading, rendered as `<h2>`. */
  title?: string
  itemColor?: string
  /** Rich approved answers keep their inline links; `answer` remains the plain JSON-LD sentence. */
  renderAnswer?: (item: FaqItem, index: number) => ReactNode
}

/**
 * Lightweight FAQ built on native `<details>/<summary>` (PRD §20, §37): keyboard and screen-reader
 * accessible by default, no JS, and every answer is in the server HTML even while collapsed.
 * Visuals follow `components/sections/AccordionNewSection`.
 */
export const FaqAccordion: FC<FaqAccordionProps> = ({
  items,
  title,
  itemColor = 'grayscale.20',
  renderAnswer,
  ...rest
}) => {
  if (!items.length) return null

  return (
    <Box as="section" color="phthalo" {...rest}>
      {title && (
        <H2 textAlign={{ base: 'center', lg: 'left' }} mb={{ base: '1.5rem', lg: '3rem' }}>
          {title}
        </H2>
      )}
      {items.map(({ question, answer }, index) => (
        <Box
          as="details"
          key={question}
          rounded="24px"
          bg={itemColor}
          mb={index === items.length - 1 ? 0 : 3}
          sx={{
            '& > summary': { listStyle: 'none' },
            '& > summary::-webkit-details-marker': { display: 'none' },
            '& > summary .medleve-faq-chevron': { transition: 'transform 0.2s ease', transform: 'rotate(-90deg)' },
            '&[open] > summary .medleve-faq-chevron': { transform: 'rotate(0deg)' }
          }}
        >
          <Box
            as="summary"
            display="flex"
            alignItems="flex-start"
            cursor="pointer"
            rounded="24px"
            p={6}
            _focusVisible={{ outline: '2px solid', outlineColor: 'medleve', outlineOffset: '2px' }}
          >
            <H3
              flex={1}
              textAlign="left"
              fontWeight={800}
              fontSize={{ base: '1rem', lg: '1.5rem' }}
              letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
              lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
            >
              {question}
            </H3>
            <Box className="medleve-faq-chevron" pl={4} aria-hidden="true">
              <NewChevronDown width="16px" fill="phthalo" />
            </Box>
          </Box>
          <Box px={6} pb={6}>
            <Body1 whiteSpace={renderAnswer ? 'pre-line' : undefined}>
              {renderAnswer ? renderAnswer(items[index], index) : answer}
            </Body1>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default FaqAccordion
